// AgentQuota (AIAgent_AgentQuotas) sub-entity client — restores the "Quotas" Agent tab dropped in
// the 2026-08-19/20 AgentEditor.tsx refactor. Targets the real live backend
// (BizFirst.Ai.AiAgent.Api.Base's BaseAgentQuotaController), verified by reading the controller
// and entity directly.
//
// ROUTE DRIFT FOUND — the task's own briefing said the base route was
// `/api/v1/ai-agent/quotas` ("verify this"). Reading BaseAgentQuotaController.cs directly shows
// `[Route("api/v1/ai-agent/agent-quotas")]` — `/agent-quotas`, not `/quotas`. Trusting the live
// backend; documented in DevelopmentHistoryLog.md.
//
// Fields confirmed exactly as briefed: the real AgentQuota entity
// (BizFirst.Ai.AiAgent.Domain.Entities.AgentQuota) has only AgentID + a required QuotaTypeID FK +
// a required decimal Value — no QuotaName/QuotaPeriod enum/CurrentUsage rollup.
//
// getQuotasByAgent posts to `by-agent` (confirmed on BaseAgentQuotaController.cs), same
// `{ AgentID: { ID } }` shape as every other by-agent query in this package.

import { BaseAdminApiClient, type AdminApiConfig, type AdminApiResult } from './baseAdminApiClient';
import type { AgentQuotaDTO, AgentQuotaFormValues } from '../types';

export class AgentQuotaAdminApiClient extends BaseAdminApiClient {
  constructor(config: AdminApiConfig) {
    super(config);
  }

  async getQuotasByAgent(agentId: number): Promise<AdminApiResult<AgentQuotaDTO[]>> {
    const result = await this.postQuery<AgentQuotaDTO[]>('/api/v1/ai-agent/agent-quotas/by-agent', {
      AgentID: { ID: agentId },
      PageInfo: { PageNumber: 1, PageSize: 1000 },
    });
    return { ...result, data: Array.isArray(result.data) ? result.data : [] };
  }

  async createQuota(agentId: number, quota: AgentQuotaFormValues): Promise<AdminApiResult<AgentQuotaDTO>> {
    return this.post<AgentQuotaDTO>('/api/v1/ai-agent/agent-quotas', { AgentID: agentId, ...quota });
  }

  async updateQuota(agentQuotaId: number, quota: Partial<AgentQuotaFormValues>): Promise<AdminApiResult<AgentQuotaDTO>> {
    return this.put<AgentQuotaDTO>(`/api/v1/ai-agent/agent-quotas/${agentQuotaId}`, {
      AgentQuotaID: agentQuotaId,
      ...quota,
    });
  }

  async deleteQuota(agentQuotaId: number): Promise<AdminApiResult<void>> {
    return this.del<void>(`/api/v1/ai-agent/agent-quotas/${agentQuotaId}`, { id: agentQuotaId });
  }
}
